import type { AxiosInstance } from "axios";
import { dashboardRouterEvents } from "@/hooks/useDashboardRouter";
import { isBotWebVitalsClient } from "@/lib/reportWebVital";
import {
  hardLoadProofLanding,
  isProofPane,
  landingPlace,
  proofReportChild,
  type ProofLanding,
} from "./proofLanding";

// In the root bundle (usePrefetchDeepLinkedSession begins it), so it imports
// nothing heavier than sessionOpenTiming does.

const METRICS_URL = "/frontend/metrics";

type EvidenceKind = "image" | "video";
type EvidenceSurface = "inline" | "gallery";

let api: AxiosInstance | null = null;
let landing: ProofLanding | null = null;
let begun = false;
let done = false;
let reportJudged = false;
let hiddenSeen = false;
let galleryOpen = false;
/** The workspace artifact (report or one of its rounds) the landing first showed. */
let landingArtifactId: string | null = null;
let observer: IntersectionObserver | null = null;
/** The captures the landing report rendered first; ones streamed in later are a running Proof's. */
const eligibleUrls = new Set<string>();
const loadedAt = new Map<
  Element,
  { at: number; kind: EvidenceKind; surface: EvidenceSurface }
>();
/** In view but under `visibility: hidden`; only a fresh note checks them again. */
const undisplayed = new Set<Element>();

function onVisibilityChange() {
  if (document.visibilityState === "hidden") hiddenSeen = true;
}

/**
 * Times a hard load that opens a Proof report, from navigation start to the
 * first screenshot or recording visible in the report. Called once at app mount,
 * so a tab hidden at any point afterwards is left out.
 */
export function beginProofFirstEvidence(nextApi: AxiosInstance): void {
  if (begun) return;
  begun = true;
  const proofLanding = hardLoadProofLanding();
  if (proofLanding === null || isBotWebVitalsClient()) return;
  landing = proofLanding;
  api = nextApi;
  hiddenSeen = document.visibilityState === "hidden";
  document.addEventListener("visibilitychange", onVisibilityChange);
  dashboardRouterEvents.on("routeChangeStart", onRouteChangeStart);
  window.addEventListener("popstate", onPopState);
}

/** Leaving the landing report ends the measurement for the rest of the document. */
function abandon(): void {
  done = true;
  observer?.disconnect();
  observer = null;
  loadedAt.clear();
  undisplayed.clear();
  eligibleUrls.clear();
  galleryOpen = false;
  landingArtifactId = null;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  dashboardRouterEvents.off("routeChangeStart", onRouteChangeStart);
  window.removeEventListener("popstate", onPopState);
}

function opensOtherReport(pane: string | null): boolean {
  const child = proofReportChild(pane);
  return (
    landing?.reportId != null && child !== null && child !== landing.reportId
  );
}

// A report opened later by in-app navigation would otherwise be timed from the hard load.
function isLanding(url: URL): boolean {
  const pane = url.searchParams.get("pane");
  return (
    landing !== null &&
    landingPlace(url.pathname) === landing.place &&
    isProofPane(pane) &&
    !opensOtherReport(pane)
  );
}

function stillOnLanding(): boolean {
  return isLanding(new URL(window.location.href));
}

// The app's own pane writes on load (catalog setup, the PR Proof tab rewrite) are
// shallow and can pass through a non-Proof pane; a non-shallow same-path navigation is a
// link click, a detour whose time would inflate the sample.
function onRouteChangeStart(
  destination: string,
  options?: { shallow?: boolean },
) {
  const url = new URL(destination, window.location.href);
  if (
    landingPlace(url.pathname) !== landing?.place ||
    opensOtherReport(url.searchParams.get("pane")) ||
    (!isLanding(url) && options?.shallow !== true)
  ) {
    abandon();
  }
}

/** The user picked a workspace pane (tab, open, close); one that is not the landing report is a detour. */
export function noteWorkspacePaneSelected(pane: string | null): void {
  if (landing === null || done) return;
  if (!isProofPane(pane) || opensOtherReport(pane)) abandon();
}

function onPopState() {
  if (!stillOnLanding()) abandon();
}

// IntersectionObserver counts `visibility: hidden` (ProofPanel's inactive tabs) as in view.
function displayed(element: Element): boolean {
  return getComputedStyle(element).visibility !== "hidden";
}

function report(kind: EvidenceKind, totalMs: number) {
  if (done) return;
  const source = landing?.source ?? null;
  const proofSessionId = landing?.reportId ?? null;
  abandon();
  if (source === null || hiddenSeen || document.visibilityState === "hidden") {
    return;
  }
  void api
    ?.post(METRICS_URL, {
      codesmith_proof_first_evidence: {
        source,
        kind,
        total_ms: Math.round(totalMs),
        proof_session_id: proofSessionId,
      },
    })
    .catch(() => {});
}

function onIntersect(entries: IntersectionObserverEntry[]) {
  if (!stillOnLanding()) {
    abandon();
    return;
  }
  for (const entry of entries) {
    const loaded = loadedAt.get(entry.target);
    if (!entry.isIntersecting || !loaded) continue;
    if (!displayed(entry.target)) {
      undisplayed.add(entry.target);
      continue;
    }
    report(loaded.kind, Math.max(loaded.at, entry.time));
    return;
  }
}

/**
 * The pane showed a report artifact. A run still starting shows no report yet,
 * so its captures would stream in; switching to another round of the same
 * report (a user's run pick) is a detour, like another pane.
 */
export function noteProofReportPaneShown(
  reportId: string,
  artifactId: string,
  hasBody: boolean,
): void {
  if (landing === null || done) return;
  if (landing.reportId !== null && reportId !== landing.reportId) return;
  if (!hasBody) {
    if (!reportJudged) abandon();
    return;
  }
  landingArtifactId ??= artifactId;
  if (artifactId !== landingArtifactId) abandon();
}

/**
 * A pane report rendered its data. The landing's first render decides the
 * measurement and freezes which captures count: a capture that streams in
 * later from a running Proof would time its run, minutes later, as a cold
 * load. A PR workspace `proof:` landing binds to the first report it renders.
 */
export function noteProofReportRendered(
  reportId: string,
  captureUrls: readonly string[],
): void {
  if (landing === null || done || reportJudged) return;
  if (!stillOnLanding()) {
    abandon();
    return;
  }
  landing.reportId ??= reportId;
  if (reportId !== landing.reportId) return;
  reportJudged = true;
  if (captureUrls.length === 0) {
    abandon();
    return;
  }
  for (const url of captureUrls) eligibleUrls.add(url);
}

/**
 * The fullscreen gallery covers the page, so while it is open only its capture
 * counts. Dismissing it before any evidence is shown ends the measurement: later
 * timing would include the time spent reading.
 */
export function noteProofGalleryOpen(open: boolean): void {
  if (landing === null || done) return;
  if (!open) {
    if (galleryOpen) abandon();
    return;
  }
  galleryOpen = true;
  for (const [element, loaded] of loadedAt) {
    if (loaded.surface !== "inline") continue;
    loadedAt.delete(element);
    undisplayed.delete(element);
    observer?.unobserve(element);
  }
}

/** The gallery moved off the capture it opened on; the next one's time would include viewing the first. */
export function noteProofGalleryStepped(): void {
  if (landing !== null && !done && galleryOpen) abandon();
}

/**
 * The element showing the capture at `url` finished loading and is meant to be
 * seen; urls outside the landing's first render are ignored. Counts once it is also
 * in the viewport, so evidence below the fold waits until it scrolls into view.
 * Callers note again when loaded evidence becomes visible; a repeat for an
 * element still waiting on the viewport is a no-op.
 */
export function noteProofEvidenceShown(
  element: Element,
  kind: EvidenceKind,
  reportId: string,
  url: string,
  surface: EvidenceSurface,
): void {
  if (landing === null || done) return;
  if (!stillOnLanding()) {
    abandon();
    return;
  }
  if (!reportJudged || reportId !== landing.reportId) return;
  if (!eligibleUrls.has(url) || (galleryOpen && surface === "inline")) return;
  if (loadedAt.has(element) && !undisplayed.delete(element)) return;
  if (typeof IntersectionObserver === "undefined") {
    report(kind, performance.now());
    return;
  }
  loadedAt.set(element, { at: performance.now(), kind, surface });
  observer ??= new IntersectionObserver(onIntersect);
  observer.unobserve(element); // a fresh observe queues a new entry even if nothing moved
  observer.observe(element);
}
